import { Button, Form, Select } from 'antd';
import type { FormInstance } from 'antd';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { OpenClass, OpenClassStatus } from '../../openClasses/types/OpenClass';
import {
  formatSessionTimeRange,
  openClassOptions,
  certificateTemplateOptions,
} from '../../openClasses/utils/openClass';
import { remainingSeats } from '../utils/openClassAssignment';

export type OpenClassAssignmentFormValues = {
  openClassId?: number;
  certificateTemplateId?: number;
  sessionIds?: number[];
};

type SessionButtonGridProps = {
  openClass?: OpenClass;
  value?: number[];
  onChange?: (sessionIds: number[]) => void;
};

const SessionButtonGrid: React.FC<SessionButtonGridProps> = ({
  openClass,
  value,
  onChange,
}) => {
  const { t } = useTranslation();
  const selected = new Set(value ?? []);
  const toggleSession = (sessionId: number) =>
    onChange?.(
      selected.has(sessionId)
        ? (value ?? []).filter((id) => id !== sessionId)
        : [...(value ?? []), sessionId],
    );

  return (
    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
      {(openClass?.sessions ?? [])
        .filter(
          (session) =>
            session.status !== OpenClassStatus.Canceled ||
            selected.has(session.id),
        )
        .map((session) => {
          const remaining = remainingSeats(session);
          const location = session.location ? ` · ${session.location}` : '';
          return (
            <Button
              aria-pressed={selected.has(session.id)}
              className="h-auto min-h-12 whitespace-normal px-3 py-2 text-left"
              disabled={
                session.status === OpenClassStatus.Canceled ||
                (remaining <= 0 && !selected.has(session.id))
              }
              key={session.id}
              type={selected.has(session.id) ? 'primary' : 'default'}
              onClick={() => toggleSession(session.id)}>
              {`${formatSessionTimeRange(session)}${location} · ${t('views.OpenClassBookingOrderDetails.seatsLeft', { count: remaining })}`}
            </Button>
          );
        })}
    </div>
  );
};

type OpenClassAssignmentFieldsProps = {
  form: FormInstance<OpenClassAssignmentFormValues>;
  openClasses: OpenClass[];
  // A past or canceled class stays listed, disabled, so its registration still renders.
  retainedOpenClassId?: number;
  // No transfer support in the backend, so an existing registration's class is fixed.
  disableOpenClass?: boolean;
  disabledOpenClassIds?: number[];
};

const OpenClassAssignmentFields: React.FC<OpenClassAssignmentFieldsProps> = ({
  form,
  openClasses,
  retainedOpenClassId,
  disableOpenClass,
  disabledOpenClassIds,
}) => {
  const { t } = useTranslation();
  const selectedOpenClassId = Form.useWatch('openClassId', form);

  const selectedOpenClass = openClasses.find(
    ({ id }) => id === selectedOpenClassId,
  );

  const classOptions = useMemo(
    () =>
      openClassOptions(openClasses, {
        disabledOpenClassIds,
        retainedOpenClassId,
      }),
    [disabledOpenClassIds, openClasses, retainedOpenClassId],
  );

  const certificateOptions = certificateTemplateOptions(
    selectedOpenClass?.certificateTemplates,
  );

  return (
    <>
      <Form.Item
        label={t('views.OpenClassBookingOrderDetails.mapToOpenClass')}
        name="openClassId"
        rules={[{ required: true }]}>
        <Select
          disabled={disableOpenClass}
          options={classOptions}
          showSearch
          optionFilterProp="label"
          onChange={() =>
            form.setFieldsValue({
              certificateTemplateId: undefined,
              sessionIds: [],
            })
          }
        />
      </Form.Item>
      <Form.Item
        label={t('views.OpenClasses.certificateType')}
        name="certificateTemplateId"
        rules={[{ required: true }]}>
        <Select disabled={!selectedOpenClass} options={certificateOptions} />
      </Form.Item>
      <Form.Item
        label={t('views.OpenClassBookingOrderDetails.sessions')}
        name="sessionIds"
        rules={[
          {
            required: true,
            type: 'array',
            min: 1,
            message: t('views.OpenClassBookingOrderDetails.selectSession'),
          },
        ]}>
        <SessionButtonGrid openClass={selectedOpenClass} />
      </Form.Item>
    </>
  );
};

export default OpenClassAssignmentFields;
